import axios, { AxiosResponse } from "axios";

import md5 from "md5";
import { ENV } from "../constants/environment.base";
import { Lucia } from "../types/lucia";
import { IUser } from "../types/types";

export class AuthService {
  private readonly API_ROUTE_PREFIX: string = "auth";
  private readonly API_PREFIX: string =
    this.API_ROUTE_PREFIX !== ""
      ? `${ENV.API_ENDPOINT}/${this.API_ROUTE_PREFIX}`
      : ENV.API_ENDPOINT;

  /**
   * Signup user
   *
   * @param email User email address
   * @param password User password
   * @param first_name User first name
   * @param last_name User last name
   * @param phone_number User phone number
   *
   * @returns Promise with customer signup response
   */
  public signup: ({
    email,
    password,
    first_name,
    last_name,
    phone_number,
  }: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number: string;
  }) => Promise<
    AxiosResponse<{
      user: IUser;
      session: Lucia.Session;
      session_cookie: Lucia.Cookie;
    }>
  > = (data) => {
    return axios.post(`${this.API_PREFIX}/signup`, {
      data: {
        ...data,
        password: md5(data.password),
      },
    });
  };

  /**
   * Login user
   *
   * @param email User email address
   * @param password User password
   *
   * @returns Promise with customer login response
   */
  public login: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<
    AxiosResponse<{
      session: Lucia.Session;
      session_cookie: Lucia.Cookie;
    }>
  > = ({ email, password }) => {
    return axios.post(`${this.API_PREFIX}/login`, {
      email,
      password: md5(password),
    });
  };

  /**
   * Request Reset password
   *
   * @param email User email address
   *
   * @returns Promise with reset password response
   */
  public requestResetPassword: (email: string) => Promise<
    AxiosResponse<{
      sent: boolean;
    }>
  > = (email) => {
    return axios.get(`${this.API_PREFIX}/reset-password`, {
      params: { email },
    });
  };

  /**
   * Reset password
   *
   * @param token Reset password token
   * @param password New password
   *
   * @returns Promise with reset password response
   */
  public resetPassword: ({
    token,
    password,
  }: {
    token: string;
    password: string;
  }) => Promise<
    AxiosResponse<{
      session: Lucia.Session;
      session_cookie: Lucia.Cookie;
    }>
  > = ({ token, password }) => {
    return axios.post(`${this.API_PREFIX}/reset-password/${token}`, {
      password: md5(password),
    });
  };
}
